import { Pipe, type PipeTransform, inject } from "@angular/core";
import { MerchantsService } from "../services/merchants/merchants.service";

// ISO codes whose "narrow" symbol is the ambiguous "$" in their native locale
// (e.g. MXN renders as "$" in es-MX). We swap the symbol post-format so users
// see "MX$" / "CA$" instead of the generic "$".
const CURRENCY_SYMBOL_OVERRIDES: Record<string, string> = {
	MXN: "MX$",
	CAD: "CA$",
};

@Pipe({
	name: "currencyWrapper",
	standalone: true,
})
export class CurrencyPipeWrapper implements PipeTransform {
	merchantsService = inject(MerchantsService);
	transform(value: any, digitsInfo?: string, ...args: any[]): string {
		const currency = this.merchantsService.currency();
		const iso = currency?.iso ?? "USD";
		const decimal = currency?.decimal ?? 2;
		const locale = this.merchantsService.locale();
		return formatCurrencyWithOverride(Number(value), iso, decimal, locale);
	}
}

/**
 * Formats a numeric amount using Intl currency formatting, then swaps the
 * generic "$" symbol for an unambiguous one (e.g. "MX$" for MXN in es-MX).
 */
export function formatCurrencyWithOverride(
	value: number,
	iso: string,
	decimal: number,
	locale: string,
): string {
	const override = resolveOverrideSymbol(iso, locale);
	try {
		const formatter = new Intl.NumberFormat(locale, {
			style: "currency",
			currency: iso,
			currencyDisplay: "symbol",
			minimumFractionDigits: decimal,
			maximumFractionDigits: decimal,
		});
		if (!override) return formatter.format(value);
		return formatter
			.formatToParts(value)
			.map((part) => (part.type === "currency" ? override : part.value))
			.join("");
	} catch {
		return String(value);
	}
}

/** Returns the display symbol for an ISO/locale combination (e.g. "MX$"). */
export function resolveCurrencySymbol(iso: string, locale: string): string {
	const override = resolveOverrideSymbol(iso, locale);
	if (override) return override;
	try {
		return (
			new Intl.NumberFormat(locale, {
				style: "currency",
				currency: iso,
				currencyDisplay: "symbol",
			})
				.formatToParts(0)
				.find((part) => part.type === "currency")?.value ?? "$"
		);
	} catch {
		return "$";
	}
}

function resolveOverrideSymbol(iso: string, locale: string): string | undefined {
	const byIso = CURRENCY_SYMBOL_OVERRIDES[iso];
	if (byIso) return byIso;
	const countries = [extractCountry(locale), extractCountry(readStoredLocale())];
	if (countries.includes("MX")) return "MX$";
	if (countries.includes("CA")) return "CA$";
	return undefined;
}

function extractCountry(locale: string | undefined): string | undefined {
	if (!locale) return undefined;
	const parts = locale.replace(/_/g, "-").split("-");
	return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : undefined;
}

function readStoredLocale(): string | undefined {
	try {
		return typeof localStorage !== "undefined"
			? localStorage.getItem("locale") ?? undefined
			: undefined;
	} catch {
		return undefined;
	}
}
